<template>
  <v-container fluid>
    <v-layout row wrap align-baseline>
      <v-flex md2 mr-3>
        <v-autocomplete :label="$t('type')" v-model="type" :items="types" item-value="key" item-text="text">
        </v-autocomplete>
      </v-flex>
      <v-flex md2>
        <v-text-field
          :label="$t('search')"
          single-line
          clearable
          hide-details
          @keyup.enter="searchTransitions"
          v-model.trim="search"
        ></v-text-field>
      </v-flex>
      <v-flex md2>
        <v-btn color="primary" @click="searchTransitions" :disabled="!search">{{$t('search')}}</v-btn>
      </v-flex>
    </v-layout>
    <v-data-table :headers="headers" :items="transitions" :pagination.sync="pagination" :rows-per-page-items="rowPerPage">
      <template v-slot:items="props">
        <td class="text-xs-left">{{ props.item.paymentId || '' }}</td>
        <td class="text-xs-left">{{ props.item.card_name || '' }}</td>
        <td class="text-xs-left">{{ props.item.cardType || '' }}</td>
        <td class="text-xs-left">{{ props.item.clientName }}</td>
        <td class="text-xs-left">{{ props.item.amount }}</td>
        <td class="text-xs-left">{{ props.item.balance || '---' }}</td>
        <td class="text-xs-left">{{ props.item.last4 || 'N/A' }}</td>
        <td class="text-xs-left">{{ props.item.type }}</td>
        <td class="text-xs-left">{{ toTimestampString(props.item.createTime) }}</td>
        <td class="text-xs-left">{{ props.item.expense0 || 0 }}</td>
        <td class="text-xs-left">{{ props.item.expense1 || 0 }}</td>
        <td class="text-xs-center">
          <v-btn flat @click="showAdjustDialog(props.item)">{{ $t('adjust') }}</v-btn>
        </td>
      </template>
    </v-data-table>
    <AdjustBalancePopup
      v-if="adjustBalanceDialog"
      v-model="adjustBalanceDialog"
      :adjustment="adjustBalance"
      :actionFunc="addAdjustBalance"
    />
  </v-container>
</template>

<script>
import { checkRules, toTimestampString, splitKeyword } from '@/utils/tools'
import add from 'date-fns/add'
import AdjustBalancePopup from './AdjustBalancePopup'
export default {
  components: {AdjustBalancePopup},
  name: 'SystemTransaction',
  mixins: [checkRules],
  data () {
    return {
      type: 'last4',
      types: [{text: 'Last4', key: 'last4'}, {text: this.$t('paymentId'), key: 'paymentId'}],
      search: '',
      transitions: [],
      adjustBalance: {},
      selectedBalance: {},
      adjustBalanceDialog: false,
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: -1,
        sortBy: 'createTime'
      }
    }
  },
  watch: {
    adjustBalanceDialog (val) {
      if (!val) this.reset()
    }
  },
  methods: {
    addAdjustBalance () {
      let payload = {
        clientKey: this.selectedBalance.clientKey,
        clientName: this.selectedBalance.clientName,
        amount: this.adjustBalance.amount,
        note: this.adjustBalance.note,
        type: this.adjustBalance.type,
        keywords: splitKeyword(this.adjustBalance.keywords),
        attachments: this.adjustBalance.attachments || []
      }
      return this.$store.dispatch('adjustSystemBalance', payload)
    },
    reset () {
      this.adjustBalance.amount = 0
      this.adjustBalance.note = ''
      this.adjustBalance.type = ''
      this.adjustBalance.keywords = ''
    },
    showAdjustDialog (item) {
      this.selectedBalance = item
      this.adjustBalanceDialog = true
    },
    getExpense (expenseHistory, num) {
      if (!expenseHistory) return 0
      let currentTime = add(new Date(), {months: -num})
      let curKeyStr = `${currentTime.getFullYear()}-${currentTime.getMonth() + 1}`
      let target = expenseHistory.find(item => item.dateKeyStr === curKeyStr)
      return target ? target['expense'] : 0
    },
    getExpenseHeader (num) {
      let currentTime = add(new Date(), {months: -num})
      return `${currentTime.getFullYear()}-${currentTime.getMonth() + 1} Exp`
    },
    toTimestampString,
    async searchTransitions () {
      if (!this.search) return
      let transitions = await this.$store.dispatch('searchSystemTransactions', {value: this.search, field: this.type, limit: this.pagination.rowsPerPage === -1 ? 100 : this.pagination.rowsPerPage})
      this.transitions = transitions.map(transaction => {
        let {clientKey} = transaction
        let balanceStat = this.balanceMap.get(clientKey)
        let expense0 = balanceStat && this.getExpense(balanceStat.expenseHistory, 0)
        let expense1 = balanceStat && this.getExpense(balanceStat.expenseHistory, 1)
        if (balanceStat) {
          return {...transaction, balance: balanceStat.balance, expense0, expense1}
        }
        return {
          ...transaction,
          balance: '---',
          expense0: '---',
          expense1: '---'
        }
      }).sort((a, b) => a.createTime > b.createTime ? -1 : 1)
      this.search = ''
    }
  },
  computed: {
    balanceMap () {
      let systemBalances = this.$store.getters.systemBalances || []
      return new Map(systemBalances.map(item => [item.clientKey, item]))
    },
    rowPerPage () {
      return [10, 20, {text: this.$t('all'), value: -1}]  
    },
    headers () {
      return [
        { text: this.$t('paymentId'), value: 'transaction_id', align: 'left', sortable: true },
        { text: this.$t('card_name'), value: 'card_name', align: 'left', sortable: true },
        { text: this.$t('cardType'), value: 'cardType', align: 'left', sortable: true },
        { text: this.$t('clientName'), value: 'clientName', align: 'left', sortable: true },
        { text: this.$t('amount'), value: 'amount', align: 'left', sortable: true },
        { text: this.$t('balance'), value: 'balance', align: 'left', sortable: true },
        { text: 'Last4', value: '', align: 'left', sortable: false },
        { text: this.$t('type'), value: 'type', align: 'left', sortable: false },
        { text: this.$t('transactionTime'), value: 'createTime', align: 'left', sortable: true },
        { text: `${this.getExpenseHeader(0)}`, value: 'expense0', align: 'left', sortable: true },
        { text: `${this.getExpenseHeader(1)}`, value: 'expense1', align: 'left', sortable: true },
        { text: this.$t('actions'), value: 'email', align: 'center', sortable: false, width: '10%' }
      ]
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/SystemTransaction.vue